import { CompanyWebFactRow } from "@/components/company/company-web-fact-sections";
import InlineCopyValue from "@/components/company/shared/inline-copy-value";
import KeyValueRow from "@/components/company/shared/key-value-row";
import RowsCard from "@/components/company/shared/rows-card";
import SectionHeaderWithIcon from "@/components/company/shared/section-header-with-icon";
import useCopyToClipboard from "@/hooks/useCopyToClipboard";
import { useTranslation } from "@/hooks/useTranslation";
import {
  factKey,
  type LinkedCompanyWebFact,
} from "@/lib/company-web-facts";
import type {
  CompanyWebLegalRow,
  CompanyWebLegalRowId,
} from "@/lib/company-web-legal";
import { cn } from "@/lib/utils";
import { ScaleIcon } from "lucide-react";

const ROW_LABEL_KEYS: Record<CompanyWebLegalRowId, string> = {
  legalForm: "company.insights.legalForm",
  capital: "company.insights.capital",
  siren: "company.insights.identity.siren",
  siret: "company.insights.identity.siret",
  naf: "company.insights.identity.nafCode",
};

/**
 * "Legal information" in one place: the identity as label/value rows (legal
 * form, capital, identifiers to copy, NAF code over its label), then what
 * happened to the legal structure, as sourced statements. Nothing to show, no
 * section.
 */
export default function CompanyWebLegalSection({
  rows,
  facts = [],
  isWriting = false,
}: {
  rows: CompanyWebLegalRow[];
  facts?: LinkedCompanyWebFact[];
  isWriting?: boolean;
}) {
  const { t } = useTranslation();
  const [copiedValue, copy] = useCopyToClipboard();
  if (rows.length === 0 && facts.length === 0) return null;

  return (
    <section>
      <SectionHeaderWithIcon
        icon={ScaleIcon}
        title={t("company.webPreview.sections.legal")}
      />
      <RowsCard>
        {rows.map((row) => (
          <div key={row.id} className={cn(isWriting && "blur-in-appear")}>
            <KeyValueRow label={t(ROW_LABEL_KEYS[row.id])}>
              <LegalRowValue row={row} copiedValue={copiedValue} onCopy={copy} />
            </KeyValueRow>
          </div>
        ))}
        {facts.map((fact) => (
          <CompanyWebFactRow
            key={factKey(fact)}
            fact={fact}
            isWriting={isWriting}
          />
        ))}
      </RowsCard>
    </section>
  );
}

/** An identifier to copy, a value over its detail, or the bare value. */
function LegalRowValue({
  row,
  copiedValue,
  onCopy,
}: {
  row: CompanyWebLegalRow;
  copiedValue: string | null;
  onCopy: (value: string) => void;
}) {
  if (row.copyValue) {
    return (
      <InlineCopyValue
        value={row.value}
        copyValue={row.copyValue}
        copiedValue={copiedValue}
        onCopy={onCopy}
      />
    );
  }
  if (row.detail) {
    return (
      <span className="flex flex-col items-end">
        {row.value}
        <span className="text-xs font-normal text-muted-foreground">
          {row.detail}
        </span>
      </span>
    );
  }
  return row.value;
}
